import { useDateField } from '@mui/x-date-pickers/DateField/useDateField'
import { useLocalizationContext } from '@mui/x-date-pickers/internals'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useRef, useState } from 'react'

import { DatePickerUnknownAccuracy } from '../components'
import { useRtlLocaleCheck } from '.'

/** Section keys ordered from least precise to most precise. */
const PRECISION_ORDER: SectionKey[] = ['year', 'month', 'day']

/** Default values used when a section is unknown. */
const SECTION_DEFAULTS: Record<SectionKey, string> = {
  year: '2000',
  month: '01',
  day: '01',
}

/** Main hook for the date input. Day, month or year can be unknown. */
export function useUnknownDateInputMask({
  date,
  accuracy,
  onChange,
}: {
  date: DateTime | null
  accuracy: DatePickerUnknownAccuracy
  onChange: (d: DateTime | null, g: DatePickerUnknownAccuracy) => void
}) {
  const {
    utils,
    localeText,
    defaultDates: { minDate, maxDate },
  } = useLocalizationContext<DateTime>()

  const locale = utils.locale

  const dayPh = localeText.fieldDayPlaceholder()
  const monthPh = localeText.fieldMonthPlaceholder({ contentType: 'digit' })
  const yearPh = localeText.fieldYearPlaceholder({ digitAmount: 4 })

  const field = useDateField({ props: { value: date } })
  const placeholder = (field.placeholder as string) || ''

  const initialValue = date && !date.isValid ? '' : field.value

  const sections = useMemo(
    () => getSections(placeholder, dayPh, monthPh, yearPh),
    [placeholder, dayPh, monthPh, yearPh],
  )

  const placeholders: SectionValues = useMemo(
    () => ({ day: dayPh, month: monthPh, year: yearPh }),
    [dayPh, monthPh, yearPh],
  )

  const [sectionValues, setSectionValues] = useState<SectionValues>(() =>
    initialValue ? extractSections(initialValue, sections) : { ...placeholders },
  )

  const [focused, setFocused] = useState(false)

  const currentDate = getCurrentDate(sectionValues, placeholders, locale)
  const underMin = currentDate < minDate
  const overMax = currentDate > maxDate
  const externalInvalid = date && !date.isValid
  const [internalInvalid, setInternalInvalid] = useState(false)

  const buffersRef = useRef<Record<SectionKey, string[]>>({
    day: [],
    month: [],
    year: [],
  })

  const prevInitialValueRef = useRef(initialValue || '')

  const localChangeRef = useRef(false)

  const inputRef = useRef<HTMLInputElement | null>(null)
  const activeSectionRef = useRef<SectionKey | null>(null)

  const select = (section: Section) => {
    activeSectionRef.current = section.key
    inputRef.current?.setSelectionRange(section.start, section.end)
  }

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(timeoutRef.current), [])

  const emitChange = (values: SectionValues) => {
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      const normalized = normalizePlaceholders(values, placeholders)
      const computedAccuracy = computeAccuracy(values, placeholders)
      if (computedAccuracy === 'unknown') {
        setInternalInvalid(false)
        onChange(null, 'unknown')
        return
      }
      const dt = DateTime.fromISO(toIsoString(normalized), { locale }) as any
      localChangeRef.current = true
      setInternalInvalid(!dt.isValid)
      onChange(dt, computedAccuracy ?? 'unknown')
    }, 0)
  }

  /** Empties the typing buffers of all sections more precise than `key`. */
  const clearMorePreciseBuffers = (key: SectionKey) => {
    const idx = PRECISION_ORDER.indexOf(key)
    for (let i = idx + 1; i < PRECISION_ORDER.length; i++) {
      buffersRef.current[PRECISION_ORDER[i]] = []
    }
  }

  /** Returns true if the cascade would change any more-precise section. */
  const cascadeNeeded = (key: SectionKey, draft: SectionValues): boolean =>
    cascadePrecision(draft, key, placeholders) !== draft

  /** Cascades placeholder/zero state downstream, clears buffers, and emits. */
  const applyCascade = (key: SectionKey, draft: SectionValues): void => {
    const next = cascadePrecision(draft, key, placeholders)
    clearMorePreciseBuffers(key)
    setSectionValues(next)
    emitChange(next)
  }

  /** Writes `value` into a section. Skips the cascade during partial typing. */
  const updateSection = (key: SectionKey, value: string, { cascade = true }: { cascade?: boolean } = {}) => {
    const draft = { ...sectionValues, [key]: value }
    if (cascade && cascadeNeeded(key, draft)) {
      applyCascade(key, draft)
      return
    }
    setSectionValues(draft)
    emitChange(draft)
  }

  const clearSection = (section: Section) => {
    buffersRef.current[section.key] = []
    updateSection(section.key, placeholders[section.key])
    requestAnimationFrame(() => select(section))
  }

  /**
   * Finalizes the section the user is leaving: clears its typing buffer and
   * cascades placeholder/zero state to more-precise sections, if applicable.
   * The cascade only fires when the section was actually edited (non-empty
   * buffer); merely navigating across an untouched section must not wipe
   * already-typed downstream values.
   */
  const commitSection = (key: SectionKey) => {
    const hadBuffer = buffersRef.current[key].length > 0
    buffersRef.current[key] = []
    if (hadBuffer && cascadeNeeded(key, sectionValues)) {
      applyCascade(key, sectionValues)
    }
  }

  const clearAll = () => {
    buffersRef.current = { day: [], month: [], year: [] }
    const next = { ...placeholders }
    setSectionValues(next)
    emitChange(next)
    requestAnimationFrame(() => select(sections[0]))
  }

  const incrementSection = (section: Section, delta: number) => {
    const current = sectionValues[section.key]
    const numeric = current === placeholders[section.key] ? 0 : parseInt(current, 10)

    const monthNum = parseInt(sectionValues.month, 10)
    const yearNum = parseInt(sectionValues.year, 10)
    const { min, max } = getSectionLimits(section.key, monthNum, yearNum)
    const next = wrapValue(numeric, delta, min, max)
    const len = section.end - section.start

    buffersRef.current[section.key] = []
    updateSection(section.key, String(next).padStart(len, '0'))
    requestAnimationFrame(() => select(section))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allSelected = inputRef.current?.selectionStart === 0 && inputRef.current?.selectionEnd === placeholder.length
    const isDelete = e.key === 'Backspace' || e.key === 'Delete'
    const isDigit = /^\d$/.test(e.key)

    if (allSelected && isDelete) {
      e.preventDefault()
      clearAll()
      return
    }

    if (allSelected && isDigit) {
      e.preventDefault()
      buffersRef.current[sections[0].key] = []
      select(sections[0])
    }

    const section = sectionAt(sections, inputRef.current?.selectionStart ?? 0)

    switch (e.key) {
      case 'ArrowRight': {
        e.preventDefault()
        const target = nextSection(sections, section.key)
        commitSection(section.key)
        requestAnimationFrame(() => select(target))
        return
      }
      case 'ArrowLeft': {
        e.preventDefault()
        const target = prevSection(sections, section.key)
        commitSection(section.key)
        requestAnimationFrame(() => select(target))
        return
      }
      case 'ArrowUp':
        e.preventDefault()
        incrementSection(section, +1)
        return
      case 'ArrowDown':
        e.preventDefault()
        incrementSection(section, -1)
        return
    }

    if (isDelete) {
      e.preventDefault()
      clearSection(section)
      return
    }

    if (!isDigit) return

    e.preventDefault()

    const buffer = buffersRef.current[section.key]
    const len = section.end - section.start
    const monthNum = parseInt(sectionValues.month, 10)
    const yearNum = parseInt(sectionValues.year, 10)
    const { min, max } = getSectionLimits(section.key, monthNum, yearNum)
    const digit = parseInt(e.key, 10)

    buffer.push(e.key)

    if (buffer.length === 1 && len === 2 && digit > Math.floor(max / 10)) {
      buffer.unshift('0')
    }

    if (buffer.length >= len && len === 2) {
      const tentative = parseInt(buffer.join(''), 10)
      if (tentative > max || tentative < min) {
        buffer.length = 0
        buffer.push(e.key)
        if (digit > Math.floor(max / 10)) {
          buffer.unshift('0')
        }
      }
    }

    const padded = buffer.slice(-len).join('').padStart(len, '0')
    const numericValue = parseInt(padded, 10)

    const bufferFull = buffer.length >= len
    const isLongField = len !== 2
    const valueReachesMin = numericValue >= min
    const isOnLastSection = isLastSection(sections, section.key)

    const shouldUpdateSection = isLongField || bufferFull || valueReachesMin
    const shouldClearBuffer = bufferFull || (shouldUpdateSection && isOnLastSection && numericValue === 0)

    if (shouldClearBuffer) {
      buffersRef.current[section.key] = []
    }

    if (shouldUpdateSection) {
      updateSection(section.key, padded, { cascade: shouldClearBuffer })
    }

    requestAnimationFrame(() => select(bufferFull ? nextSection(sections, section.key) : section))
  }

  const handleFocus = () => {
    setFocused(true)
    requestAnimationFrame(() => select(sections[0]))
  }

  const handleClick = () => {
    const target = sectionAt(sections, inputRef.current?.selectionStart ?? 0)
    const prev = activeSectionRef.current
    if (prev && prev !== target.key) {
      commitSection(prev)
      requestAnimationFrame(() => select(target))
      return
    }
    select(target)
  }

  const handleBlur = () => {
    setFocused(false)
    const active = activeSectionRef.current
    if (active) {
      commitSection(active)
    }
    activeSectionRef.current = null
  }

  const selectDate = (d: DateTime | null) => {
    setInternalInvalid(false)
    onChange(d, 'day')
  }

  useEffect(() => {
    const prevInitialValue = prevInitialValueRef.current
    prevInitialValueRef.current = initialValue || ''

    if (focused) return

    if (localChangeRef.current) {
      localChangeRef.current = false
      return
    }

    if (!initialValue) {
      if (prevInitialValue || externalInvalid) {
        setSectionValues({ ...placeholders })
        buffersRef.current = { day: [], month: [], year: [] }
      }
      return
    }

    const incoming = extractSections(initialValue, sections)

    setInternalInvalid(false)
    switch (accuracy) {
      case 'day':
        setSectionValues(incoming)
        break
      case 'month':
        setSectionValues({
          ...placeholders,
          year: incoming.year,
          month: incoming.month,
        })
        break
      case 'year':
        setSectionValues({ ...placeholders, year: incoming.year })
        break
      default:
        setSectionValues({ ...placeholders })
        break
    }
  }, [date, initialValue, accuracy, sections, placeholders, focused, externalInvalid])

  const displayString = toDisplayString(sectionValues, sections, placeholder)
  const hasError = internalInvalid || externalInvalid || underMin || overMax

  const isAllPlaceholder = (Object.keys(sectionValues) as SectionKey[]).every(
    (k) => sectionValues[k] === placeholders[k],
  )

  const displayValue = focused
    ? displayString
    : isAllPlaceholder && !hasError
    ? ''
    : hasError
    ? displayString
    : toString(displayString, date, locale, accuracy)

  const isRtl = useRtlLocaleCheck(locale)

  return {
    locale,
    displayValue,
    hasError,
    inputProps: { dir: isRtl ? 'rtl' : undefined },
    inputRef,
    handleKeyDown,
    handleFocus,
    handleClick,
    handleBlur,
    selectDate,
    placeholder,
  }
}

export type SectionKey = 'day' | 'month' | 'year'

/** One piece of the date string, with start and end position. */
export type Section = {
  key: SectionKey
  start: number
  end: number
}

type SectionValues = Record<SectionKey, string>

/** Finds where day, month and year are inside the placeholder string. */
function getSections(
  format: string,
  dayPlaceholder: string,
  monthPlaceholder: string,
  yearPlaceholder: string,
): Section[] {
  const dayStart = format.indexOf(dayPlaceholder)
  const monthStart = format.indexOf(monthPlaceholder)
  const yearStart = format.indexOf(yearPlaceholder)

  const sections: Section[] = [
    { key: 'day', start: dayStart, end: dayStart + dayPlaceholder.length },
    {
      key: 'month',
      start: monthStart,
      end: monthStart + monthPlaceholder.length,
    },
    { key: 'year', start: yearStart, end: yearStart + yearPlaceholder.length },
  ]

  return sections.sort((a, b) => a.start - b.start)
}

/** Gets the value of each section from a date string. */
function extractSections(str: string, sections: Section[]): SectionValues {
  const result = {} as SectionValues
  for (const s of sections) {
    result[s.key] = str.slice(s.start, s.end)
  }
  return result
}

/** Puts the section values back into the a date string. */
function toDisplayString(values: SectionValues, sections: Section[], template: string): string {
  let result = template
  for (const s of [...sections].sort((a, b) => b.start - a.start)) {
    result = result.slice(0, s.start) + values[s.key] + result.slice(s.end)
  }
  return result
}

/** Returns the current internal date based on section values */
const getCurrentDate = (values: SectionValues, placeholders: SectionValues, locale: string) => {
  const normalized = normalizePlaceholders(values, placeholders)
  return DateTime.fromISO(toIsoString(normalized), { locale })
}

/** Returns true if a more precise section is filled and the ancestor is not (e.g. 01/MM/20206) */
function hasMorePreciseSectionFilledBeforeAncestor(values: SectionValues, placeholders: SectionValues): boolean {
  return PRECISION_ORDER.some((key, idx) => {
    if (idx === 0) return false
    const val = values[key]
    if (val === placeholders[key]) return false
    for (let i = 0; i < idx; i++) {
      const ancestor = PRECISION_ORDER[i]
      const aVal = values[ancestor]
      if (aVal === placeholders[ancestor]) return true
    }
    return false
  })
}

/** Replaces placeholder sections with default values. */
function normalizePlaceholders(values: SectionValues, placeholders: SectionValues): SectionValues {
  if (hasMorePreciseSectionFilledBeforeAncestor(values, placeholders)) return values
  const result = {} as SectionValues
  for (const k of Object.keys(values) as SectionKey[]) {
    result[k] = values[k] === placeholders[k] ? SECTION_DEFAULTS[k] : values[k]
  }
  return result
}

/** Propagates placeholders / zeros to more-precise sections (year > month > day). */
function cascadePrecision(values: SectionValues, changedKey: SectionKey, placeholders: SectionValues): SectionValues {
  const idx = PRECISION_ORDER.indexOf(changedKey)
  const v = values[changedKey]
  const isPlaceholder = v === placeholders[changedKey]
  if (!isPlaceholder) return values
  const result = { ...values }
  for (let i = idx + 1; i < PRECISION_ORDER.length; i++) {
    const k = PRECISION_ORDER[i]
    result[k] = placeholders[k]
  }
  return result
}

/** Makes an ISO date string from normalized section values. */
function toIsoString(values: SectionValues): string {
  return [values.year, values.month, values.day].join('-')
}

/** Checks which sections are filled and returns the accuracy. */
function computeAccuracy(values: SectionValues, placeholders: SectionValues): DatePickerUnknownAccuracy | null {
  if (hasMorePreciseSectionFilledBeforeAncestor(values, placeholders)) {
    return null
  }

  const hasYear = values.year !== placeholders.year
  const hasMonth = values.month !== placeholders.month
  const hasDay = values.day !== placeholders.day

  if (hasYear && hasMonth && hasDay) return 'day'
  if (hasYear && hasMonth) return 'month'
  if (hasYear) return 'year'
  return 'unknown'
}

/** Gets the min and max value for a section. Days change with month/year. */
function getSectionLimits(key: SectionKey, month?: number, year?: number): { min: number; max: number } {
  switch (key) {
    case 'day': {
      if (month && month >= 1 && month <= 12 && year && !Number.isNaN(year) && year > 0) {
        const lastDay = DateTime.fromObject({ year, month, day: 1 }).endOf('month').day
        return { min: 1, max: lastDay }
      }
      return { min: 1, max: 31 }
    }
    case 'month':
      return { min: 1, max: 12 }
    case 'year':
      return { min: 1, max: 9999 }
  }
}

/** Gets the section at the cursor position. */
function sectionAt(sections: Section[], pos: number): Section {
  return sections.find((s) => pos >= s.start && pos <= s.end) || sections[0]
}

/** Gets the next section, or stays on the last one. */
function nextSection(sections: Section[], key: SectionKey): Section {
  const i = sections.findIndex((s) => s.key === key)
  return sections[Math.min(i + 1, sections.length - 1)]
}

/** Gets the previous section, or stays on the first one. */
function prevSection(sections: Section[], key: SectionKey): Section {
  const i = sections.findIndex((s) => s.key === key)
  return sections[Math.max(i - 1, 0)]
}

/** Returns true if this is the last section. */
function isLastSection(sections: Section[], key: SectionKey): boolean {
  return sections[sections.length - 1].key === key
}

/** Adds delta to the value. Wraps around when it goes past min or max. */
function wrapValue(current: number, delta: number, min: number, max: number): number {
  const next = current + delta
  if (next < min) return max
  if (next > max) return min
  return next
}

function toString(displayString: string, date: DateTime | null, locale: string, accuracy: DatePickerUnknownAccuracy) {
  if (!date) return displayString
  switch (accuracy) {
    case 'year':
      return date.toLocaleString(
        {
          year: 'numeric',
        },
        { locale },
      )
    case 'month':
      return date.toLocaleString(
        {
          year: 'numeric',
          month: '2-digit',
        },
        { locale },
      )
    case 'day':
      return date.toLocaleString(
        {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        },
        { locale },
      )
    default:
      return displayString
  }
}
